/**
 * Brief-open scaffold store (Scoped Brief §3.1a — brief-modal-spec).
 *
 * Holds the ephemeral AI scaffold suggestions for the unit whose Brief is open:
 * win themes (loaded on open, fast) and documents (loaded on demand via the
 * manual "✨ Suggest documents" trigger — retrieval is slow). Accepting a chip
 * creates the real theme link / pin elsewhere; this store just drops it.
 * Dismiss is session-only. Mirrors the coverage-store suggestion pattern.
 */

import { create } from 'zustand'
import { suggestBriefScaffold, type PlanSuggestion } from '@/lib/api'

interface BriefScaffoldState {
  themeSuggestions: PlanSuggestion[]
  documentSuggestions: PlanSuggestion[]
  isLoading: boolean
  isLoadingDocuments: boolean

  loadThemes: (bidId: string, artifactId: string, sectionKey: string) => Promise<void>
  loadDocuments: (bidId: string, artifactId: string, sectionKey: string) => Promise<void>
  dismiss: (id: string) => void
  clear: () => void
}

export const useBriefScaffoldStore = create<BriefScaffoldState>((set) => ({
  themeSuggestions: [],
  documentSuggestions: [],
  isLoading: false,
  isLoadingDocuments: false,

  loadThemes: async (bidId, artifactId, sectionKey) => {
    set({ isLoading: true })
    try {
      const { themeSuggestions } = await suggestBriefScaffold(bidId, { artifactId, sectionKey })
      set({ themeSuggestions, isLoading: false })
    } catch {
      set({ isLoading: false })
    }
  },

  loadDocuments: async (bidId, artifactId, sectionKey) => {
    set({ isLoadingDocuments: true })
    try {
      const { documentSuggestions } = await suggestBriefScaffold(bidId, {
        artifactId,
        sectionKey,
        includeDocuments: true,
      })
      set({ documentSuggestions, isLoadingDocuments: false })
    } catch {
      set({ isLoadingDocuments: false })
    }
  },

  dismiss: (id) =>
    set((state) => ({
      themeSuggestions: state.themeSuggestions.filter((s) => s.id !== id),
      documentSuggestions: state.documentSuggestions.filter((s) => s.id !== id),
    })),

  clear: () => set({ themeSuggestions: [], documentSuggestions: [], isLoading: false, isLoadingDocuments: false }),
}))
